import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const open = index !== null && index >= 0;

  const prev = useCallback(() => onNavigate((index - 1 + images.length) % images.length), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % images.length), [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, prev, next, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-12 h-12 flex items-center justify-center border-2 border-[#F0F0EC]/40 text-[#F0F0EC] hover:bg-[#FF4F00] hover:text-black hover:border-[#FF4F00] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute top-6 left-6 font-mono text-[11px] tracking-[0.3em] uppercase text-[#F0F0EC]/70" data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </div>
          {images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous photo"
                className="absolute left-3 sm:left-6 z-10 w-12 h-12 flex items-center justify-center border-2 border-[#F0F0EC]/40 text-[#F0F0EC] hover:bg-[#FF4F00] hover:text-black hover:border-[#FF4F00] transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next photo"
                className="absolute right-3 sm:right-6 z-10 w-12 h-12 flex items-center justify-center border-2 border-[#F0F0EC]/40 text-[#F0F0EC] hover:bg-[#FF4F00] hover:text-black hover:border-[#FF4F00] transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              data-testid="lightbox-image"
              src={images[index]?.src}
              alt={images[index]?.alt || `Photo ${index + 1}`}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[92vw] max-h-[86vh] object-contain border-2 border-[#F0F0EC]/20 select-none"
              draggable={false}
            />
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
